/**
 * Sak's first-run tour definition (docs/product/onboarding-tour/sak-tour-plan.md).
 *
 * Steps are rebuilt on every render so a language switch mid-tour re-renders
 * with fresh strings. Targets are `data-tour-id` marks on the drive surface's
 * regions (folder tree, toolbar, file list, details panel); the details panel
 * only anchors while open and the notification center anchors to the suite
 * topbar's bell — otherwise they degrade to centered cards.
 */
import { useTranslation } from 'react-i18next';
import { useFirstRunTour, type TourConfig, type TourLabels } from '@suite/tour';

export function useSakTour(): TourConfig {
  const { t } = useTranslation();
  return {
    appId: 'sak',
    version: 2,
    steps: [
      { id: 'welcome', target: '', title: t('tour.welcome.title'), body: t('tour.welcome.body'), placement: 'center' },
      { id: 'folder-tree', target: '[data-tour-id="folder-tree"]', title: t('tour.folder-tree.title'), body: t('tour.folder-tree.body'), placement: 'right' },
      { id: 'action-toolbar', target: '[data-tour-id="action-toolbar"]', title: t('tour.action-toolbar.title'), body: t('tour.action-toolbar.body'), placement: 'bottom' },
      { id: 'file-list', target: '[data-tour-id="file-list"]', title: t('tour.file-list.title'), body: t('tour.file-list.body'), placement: 'right' },
      { id: 'details-panel', target: '[data-tour-id="details-panel"]', title: t('tour.details-panel.title'), body: t('tour.details-panel.body'), placement: 'left' },
      { id: 'notification-center', target: '[aria-label="Notifications"]', title: t('tour.notification-center.title'), body: t('tour.notification-center.body'), placement: 'bottom' },
      { id: 'done', target: '', title: t('tour.done.title'), body: t('tour.done.body'), placement: 'center' },
    ],
  };
}

export function useSakTourLabels(): TourLabels {
  const { t } = useTranslation();
  return {
    next: t('tour.next'),
    back: t('tour.back'),
    skip: t('tour.skip'),
    done: t('tour.finish'),
    start: t('tour.start'),
    stepOf: (current, total) => t('tour.stepOf', { current, total }),
  };
}

/**
 * Auto-start gate, mounted inside the app tree (provider wraps the app from
 * main.tsx); the drive page mounts it behind ProtectedRoute.
 */
export function FirstRunTourGate() {
  const sakTour = useSakTour();
  const labels = useSakTourLabels();
  useFirstRunTour(sakTour, { enabled: true, labels });
  return null;
}
